import { gql, useQuery } from '@apollo/client';
import { Stack } from '@chakra-ui/react';
import { dinero } from 'dinero.js';
import * as React from 'react';

import { AccordionMembershipCard } from '@/components/AccordionMembershipCard';
import { MembershipCtaCard } from '@/components/MembershipCtaCard';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { Hummus, HummusAside, HummusMain } from '@/layouts/Hummus';

import { MembershipPage_CurrentUser } from './graphql-types/MembershipPage_CurrentUser';

const GET_USER = gql`
  query MembershipPage_CurrentUser(
    $relevantMembershipTermsInput: RelevantMembershipTermsInput
  ) {
    currentUser {
      _id
      firstName
      displayName
      canCurrentUserAddPaymentMethod
      membershipData {
        ...MembershipCtaCard_MembershipDataFragment
        ...AccordionMembershipCard_MembershipDataFragment
      }
      creditBalance
      rosterOrganization {
        _id
        currentUserOrganizationPermission {
          canManageOrganizationProfile
        }
      }
    }
  }
  ${AccordionMembershipCard.membershipDataFragment}
  ${MembershipCtaCard.membershipDataFragment}
`;

export const MembershipPage: React.FC = () => {
  const { error, loading, data } =
    useQuery<MembershipPage_CurrentUser>(GET_USER);
  const { currency } = useDefaultCurrency();

  return (
    <Hummus>
      <HummusMain>
        {loading ? (
          <Loading />
        ) : error || !data?.currentUser ? (
          <UnexpectedError />
        ) : (
          data.currentUser.membershipData && (
            <AccordionMembershipCard
              userId={data.currentUser._id}
              userName={data.currentUser.firstName}
              membershipData={data.currentUser.membershipData}
              displayName={data.currentUser.displayName}
              creditBalance={
                data.currentUser.creditBalance
                  ? dinero(data.currentUser.creditBalance)
                  : dinero({ amount: 0, currency })
              }
              canCurrentUserManageOrganizationProfile={
                data.currentUser.rosterOrganization
                  ?.currentUserOrganizationPermission
                  .canManageOrganizationProfile
              }
            />
          )
        )}
      </HummusMain>
      <HummusAside>
        <Stack spacing="4">
          {data?.currentUser?.membershipData && (
            <MembershipCtaCard
              membershipData={data.currentUser.membershipData}
              userFirstName={data.currentUser.firstName}
              shouldBeMemberView
            />
          )}
        </Stack>
      </HummusAside>
    </Hummus>
  );
};
